import React, { useEffect, useState } from "react";
import FormLayout from "../layouts/FormLayout";
import { apiFetch } from "../utils/wkCommon";
import { toPinyinSync, capitalizePinyin } from '../utils/pinyinUtil';
import DropDownArea from "../components/DropDownArea";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
function randomTwoLetters() {
  const a = LETTERS.charAt(Math.floor(Math.random() * LETTERS.length));
  const b = LETTERS.charAt(Math.floor(Math.random() * LETTERS.length));
  return `${a}${b}`;
}
function randomTwoDigits() {
  const n = Math.floor(Math.random() * 100);
  return String(n).padStart(2, "0");
}
function makeCandidate() {
  return `${randomTwoLetters()}${randomTwoDigits()}`.toUpperCase();
}

// GANTI fungsi lama dengan ini di DaftarUmat.js
async function checkUmatIDExists(apiUrl, candidate) {
  const q = `${apiUrl}/api/umats?filters%5BumatID%5D%5B$eq%5D=${encodeURIComponent(candidate)}&pagination%5BpageSize%5D=1`;
  try {
    // apiFetch mengembalikan parsed JSON atau melempar error pada non-2xx.
    const json = await apiFetch(q);
    // bentuk yang umum: { data: [ { id, attributes: {...} }, ... ] }
    const data = json?.data ?? json;
    if (Array.isArray(data)) return data.length > 0;
    // kadang Strapi mengembalikan object single di data
    if (data && (data.id || data.attributes)) return true;
    return false;
  } catch (err) {
    // apiFetch melempar Error berisi "apiFetch HTTP 403 - {...}" atau lainnya
    console.error("checkUmatIDExists: apiFetch error:", err);
    // reraising with contextual message so caller bisa menampilkan user-friendly text
    throw new Error(`Gagal cek UmatID "${candidate}": ${err.message}`);
  }
}

async function generateUniqueUmatID(apiUrl, maxAttempts = 50) {
  let attempt = 0;
  while (attempt < maxAttempts) {
    attempt++;
    const candidate = makeCandidate();
    const exists = await checkUmatIDExists(apiUrl, candidate);
    if (!exists) return candidate;
  }
  throw new Error(`Gagal menemukan UmatID unik setelah ${maxAttempts} percobaan.`);
}

export default function DaftarUmat() {
  const [form, setForm] = useState({
    umatID: "",
    chineseName: "",
    pinyinName: "",
    fullName: "",
    nickName: "",
    gender: "",
    birthdate: "",
    area: null,
    address: "",
    mobilePhone: "",
    email: "",
    sd3Hari: false, 
  });

  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  function onChange(e) {
    const { name, value } = e.target;

    if (name === "chineseName") {
      // setiap perubahan chineseName -> selalu generate pinyin
      const pinyin = capitalizePinyin(toPinyinSync(value));
      setForm(prev => ({ ...prev, chineseName: value, pinyinName: pinyin }));
      return;
    }

    if (name === "pinyinName") {
      // tetap mengizinkan manual edit pada pinyin jika perlu (tetapi akan di-overwrite saat chineseName berubah)
      setForm(prev => ({ ...prev, pinyinName: value }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      setGenerating(true);
      setMessage(null);
      try {
        const id = await generateUniqueUmatID(API_URL, 50);
        if (!mounted) return;
        setForm((p) => ({ ...p, umatID: id }));
        setMessage({ type: "success", text: `Kode Umat ter-generate otomatis: ${id}` });
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setMessage({ type: "error", text: `Gagal generate otomatis: ${err.message}` });
      } finally {
        if (mounted) setGenerating(false);
      }
    })();

    return () => (mounted = false);
  }, []);

  async function handleGenerateClick() {
    setGenerating(true);
    setMessage(null);
    try {
      const id = await generateUniqueUmatID(API_URL, 50);
      setForm((p) => ({ ...p, umatID: id }));
      setMessage({ type: "success", text: `Kode Umat ter-generate: ${id}` });
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: `Gagal generate: ${err.message}` });
    } finally {
      setGenerating(false);
    }
  }

  function sanitizePayload(obj) {
    const cleaned = {};

    for (const [key, value] of Object.entries(obj)) {
      // jika string kosong → ubah jadi null
      if (value === "") {
        cleaned[key] = null;
        continue;
      }

      cleaned[key] = value;
    }

    return cleaned;
  }

  async function submitHandler(e) {
    // FormLayout already called e.preventDefault() before invoking this.
    setMessage(null);

    const hasChinese = form.chineseName && form.chineseName.trim() !== "";
    const hasFull = form.fullName && form.fullName.trim() !== "";

    if (!hasChinese && !hasFull) {
      setMessage({
        type: "error",
        text: "Wajib mengisi Nama Mandarin atau Nama Lengkap (minimal salah satu).",
      });
      return;
    }

    if (hasChinese && (!form.pinyinName || form.pinyinName.trim() === "")) {
      setMessage({
        type: "error",
        text: "Nama Pinyin wajib diisi jika Nama Mandarin diisi.",
      });
      return;
    }

    setLoading(true);
    try {
      const cleaned = sanitizePayload(form);

      const payload = {
        ...cleaned,
        umatID: form.umatID.toUpperCase(),
        area: form.area ?? null
      };
      const result = await apiFetch(`${API_URL}/api/umats`, {
        method: "POST",
        body: { data: payload },
      });

      //if (!res.ok) {
      //  const err = await res.json().catch(() => null);
      //  const serverMsg = err?.error?.message || err?.message || `HTTP ${res.status}`;
      //  throw new Error(serverMsg);
      //}

      // `result` is parsed JSON from server (e.g. created entity)
      console.debug("[DaftarUmat] submit result:", result);

      // reset form but auto-generate new umatID for next input
      try {
        const nextId = await generateUniqueUmatID(API_URL, 50);
        setForm({
          domisiliSekitarJakarta: true,
          umatID: nextId,
          chineseName: "",
          pinyinName: "",
          fullName: "",
          nickName: "",
          gender: "",
          birthdate: "",
          area: null,
          address: "",
          mobilePhone: "",
          email: "",
          sd3Hari: false,
        });
        setMessage({ type: "success", text: "Pendaftaran berhasil. Kode baru: " + nextId });
      } catch (err2) {
        // gagal generate berikutnya, kosongkan umatID
        setForm((p) => ({ ...p, fullName: "", chineseName: "", pinyinName: "", nickName: "", gender: "", birthdate: "", area: null, address: "", mobilePhone: "", email: "" }));
        console.warn("Gagal generate ID selanjutnya:", err2);
      }

      //console.log("Created:", res);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: `Gagal submit: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }

  // LEFT panel (desktop) - full parent summary/details (example)
  const leftPanelNode = (
    <div></div>
    /* -- CONTOH -- */
    /*
    <div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Order Summary</div>
      <div style={{ marginBottom: 6 }}><strong>Order ID:</strong> ORD-123</div>
      <div style={{ marginBottom: 6 }}><strong>Customer:</strong> PT Ayo Maju Bersama</div>
      <div style={{ marginBottom: 6 }}><strong>Order Date:</strong> 2025-10-01</div>
      <div style={{ marginBottom: 6 }}><strong>Total:</strong> Rp 12.500.000</div>
      <div style={{ marginTop: 8 }}><small>Catatan: ini contoh ringkasan parent data. Sesuaikan dengan properti yang ada.</small></div>
    </div>
    */
  );

  // LEFT mobile summary (single-line) shown when collapsed on mobile
  const leftSummaryNode = (
    <div style={{ fontWeight: 600 }}>&nbsp;</div>
  );

  // RIGHT panel (desktop) - instructions / petunjuk pengisian
  const rightPanelNode = (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Petunjuk Pengisian</div>
      <ol style={{ margin: 0, paddingLeft: 16 }}>
        <li>Pastikan sudah mencari data umat sebelum mendaftar baru.</li>
        <li>Isi Kode Umat dan Nama Lengkap wajib diisi.</li>
        <li>Gunakan tombol Generate untuk membuat Kode Umat unik.</li>
        <li>Format UmatID: 2 huruf kapital + 2 angka (contoh: AB07).</li>
      </ol>
    </div>
  );

  const infoNode = message ? (
    <div style={{ padding: 8, borderRadius: 6, background: message.type === "error" ? "#ffecec" : "#e9f6ee", color: message.type === "error" ? "#9b1c1c" : "#064e2d" }}>
      {message.text}
    </div>
  ) : null;

  return (
    <FormLayout
      title="Pendaftaran Umat"
      onSubmit={submitHandler}
      submitLabel={loading ? "Menyimpan..." : "SUBMIT"}
      leftPanelNode={leftPanelNode}
      leftSummaryNode={leftSummaryNode}
      rightPanelNode={rightPanelNode}
      rightSummaryTitle="Petunjuk Pengisian"
      infoNode={infoNode}
    >
      {/* form fields - DO NOT wrap with <form> (FormLayout already provides it) */}

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>* Kode Umat</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input name="umatID" value={form.umatID} disabled onChange={(e) => setForm((p) => ({ ...p, umatID: e.target.value.toUpperCase() }))} style={{ flex: 1, padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} required />
          <button type="button" onClick={handleGenerateClick} disabled={generating} style={{ background: "#2b4a84", color: "white", border: "none", padding: "8px 12px", borderRadius: 6, cursor: generating ? "wait" : "pointer" }}>{generating ? "Mencoba..." : "Generate"}</button>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>姓名 Nama Mandarin (jika ada)</label>
        <input name="chineseName" value={form.chineseName} onChange={onChange} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Nama Pinyin (Hanyu Pinyin)</label>
        <input name="pinyinName" value={form.pinyinName} onChange={onChange} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Nama Lengkap (sesuai KTP)</label>
        <input name="fullName" value={form.fullName} onChange={onChange} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Nama Panggilan</label>
        <input name="nickName" value={form.nickName} onChange={onChange} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Jenis Kelamin</label>
        <select name="gender" value={form.gender} onChange={onChange} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }}>
          <option value="">-- pilih --</option>
          <option value="乾 Pria">乾 Pria</option>
          <option value="坤 Wanita">坤 Wanita</option>
        </select>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Tanggal Lahir</label>
        <input name="birthdate" value={form.birthdate} onChange={onChange} type="date" style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <DropDownArea
        value={form.area}
        onChange={(id) => setForm(p => ({ ...p, area: id }))}
        showLabel="true"
      />

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Address</label>
        <textarea name="address" value={form.address} onChange={onChange} rows={3} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>No. Mobile</label>
        <input name="mobilePhone" value={form.mobilePhone} onChange={onChange} style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>Email</label>
        <input name="email" value={form.email} onChange={onChange} type="email" style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label
          style={{
            display: "block",
            marginBottom: 6,
            color: "#2b4a84",
            fontWeight: 600,
          }}
        >
          Sidang Dharma 3 Hari
        </label>

        <select
          value={
            form.sd3Hari === true
              ? "true"
              : form.sd3Hari === false
              ? "false"
              : ""
          }
          onChange={(e) => {
            const v = e.target.value;
            setForm((p) => ({
              ...p,
              sd3Hari: v === "" ? false : v === "true",
            }));
          }}
          style={{
            width: "100%",
            padding: 10,
            border: "2px solid #2b4a84",
            borderRadius: 6,
          }}
        >
          <option value="">-- pilih --</option>
          <option value="true">已開 · Sudah</option>
          <option value="false">未開 · Belum</option>
        </select>
      </div>


      {/* small spacing so bottom submit button doesn't stick to inputs */}
      <div style={{ height: 8 }} />

    </FormLayout>
  );
}
